import { ENGINE_VERSION } from './constants';
import { recordChartDilution, capitalise, type ChartDilution } from './dilution';
import { recordChartExtent, type ExtentStatus } from './extent';
import { computeRelease, type ReleaseResult } from './release';
import { temperatureClassFor } from './substance';
import { summate, type Scenario, type SummationMember } from './summation';
import { q, Recorder, type Issue, type Ref, type Step } from './trace';
import { areaVentilation, type AreaVentilation } from './ventilation';
import { describeOutcome, NOTE_TEXT, zoneOutcome, type ZoneOutcome } from './zone';
import type { Area, AvailabilityLevel, Dilution, GasGroup, Grade, ProjectDoc, Source, TemperatureClass, ZoneNumber } from '../model/types';

export interface ZoneVolume {
  zone: ZoneNumber;
  role: 'single' | 'inner' | 'outer';
  /** Horizontal extent from the source, m. null when the zone fills the room or is not yet known. */
  horizontalM: number | null;
  /** Vertical extent above the source, m. */
  verticalM: number | null;
  /** The zone is the whole enclosed volume of the area. */
  roomFill: boolean;
}

export interface Prompt {
  id: string;
  question: string;
  options: Array<{ value: string; label: string }>;
  answer?: string;
  ref: Ref;
}

export interface SourceResult {
  sourceId: string;
  /** All steps computed without errors. */
  complete: boolean;
  release?: ReleaseResult;
  qc?: number;
  uw?: number;
  scenario?: Scenario;
  chart?: ChartDilution;
  dilution?: Dilution;
  dilutionBasis?: 'chart' | 'background' | 'outdoor-rule' | 'override';
  availability?: AvailabilityLevel;
  outcome?: ZoneOutcome;
  /** Table D.1 wording for the outcome. */
  outcomeLabel?: string;
  /** What is drawn: e.g. "Zone 1" once a judgement is answered. */
  zoneLabel?: string;
  negligible?: ZoneNumber;
  volumes: ZoneVolume[];
  extent?: { horizontalM: number | null; verticalM: number | null; status?: ExtentStatus; basis: 'chart' | 'room' | 'override' };
  gasGroup?: GasGroup;
  tClass?: TemperatureClass;
  steps: Step[];
  issues: Issue[];
  prompts: Prompt[];
}

export interface AreaResult {
  areaId: string;
  ventilation?: AreaVentilation;
  scenarios: Partial<Record<Grade, Scenario>>;
  steps: Step[];
  issues: Issue[];
}

export interface ProjectResults {
  engineVersion: string;
  sources: Record<string, SourceResult>;
  areas: Record<string, AreaResult>;
}

interface Prepared {
  source: Source;
  rec: Recorder;
  result: SourceResult;
}

export function assessProject(doc: ProjectDoc): ProjectResults {
  const settings = doc.settings;
  const substances = new Map(doc.substances.map((s) => [s.id, s]));
  const areas = new Map(doc.areas.map((a) => [a.id, a]));

  // 1. area ventilation
  const areaResults: Record<string, AreaResult> = {};
  for (const area of doc.areas) {
    const rec = new Recorder();
    const ventilation = areaVentilation(area, rec);
    areaResults[area.id] = { areaId: area.id, ...(ventilation ? { ventilation } : {}), scenarios: {}, steps: rec.steps, issues: rec.issues };
  }

  // 2. release rate and release characteristic for every source
  const prepared: Prepared[] = doc.sources.map((source) => {
    const rec = new Recorder();
    const result: SourceResult = { sourceId: source.id, complete: false, volumes: [], steps: rec.steps, issues: rec.issues, prompts: [] };
    const sub = substances.get(source.substanceId);
    const area = areas.get(source.areaId);
    if (!sub) rec.issue({ code: 'source.substance', severity: 'error', field: 'substanceId', message: `Choose a substance for ${source.tag}.` });
    if (!area) rec.issue({ code: 'source.area', severity: 'error', field: 'areaId', message: `Place ${source.tag} in an area.` });
    if (!sub || !area) return { source, rec, result };

    if (sub.gasGroup) result.gasGroup = sub.gasGroup;
    const tClass = temperatureClassFor(sub.aitC);
    if (tClass) result.tClass = tClass;
    const vent = areaResults[area.id]?.ventilation;
    if (!vent) {
      rec.issue({ code: 'source.ventilation', severity: 'error', field: 'areaId', message: `${area.name} has no usable ventilation data yet.` });
      return { source, rec, result };
    }
    result.uw = vent.velocity;

    const release = computeRelease(source.release, sub, {
      ambientPressurePa: settings.ambientPressurePa,
      ambientTemperatureK: settings.ambientTemperatureK,
      ventilationVelocityMPerS: vent.velocity,
    }, rec);
    if (!release) return { source, rec, result };
    result.release = release;

    const k = settings.lflSafetyFactor > 0 && settings.lflSafetyFactor <= 1 ? settings.lflSafetyFactor : 1;
    const qc = release.massRate / (release.gasDensity * k * sub.lflVolFrac);
    rec.step({
      id: 'release.characteristic',
      label: 'Volumetric release characteristic',
      ref: { clause: 'C.3.3', figure: 'C.1' },
      inputs: { Wg: q(release.massRate, 'kg/s'), ρg: q(release.gasDensity, 'kg/m3'), LFL: q(sub.lflVolFrac, 'vol/vol'), ...(k !== 1 ? { 'safety factor': q(k, '1') } : {}) },
      output: { symbol: 'Qc', ...q(qc, 'm3/s') },
      ...(k !== 1 ? { notes: [{ kind: 'decision' as const, text: `LFL multiplied by ${k} as a safety factor.`, ref: { clause: 'C.3.5', md: 'MD-007' } }] } : {}),
    });
    result.qc = qc;
    return { source, rec, result };
  });

  // 3. summation of releases in each indoor area (B.4)
  for (const area of doc.areas) {
    const ar = areaResults[area.id];
    if (!ar || area.kind !== 'indoor' || area.ventilation.kind !== 'indoor' || !ar.ventilation?.airFlow) continue;
    const members: SummationMember[] = [];
    for (const p of prepared) {
      if (p.source.areaId !== area.id || p.result.qc === undefined || !p.result.release) continue;
      const sub = substances.get(p.source.substanceId);
      if (!sub) continue;
      members.push({ sourceId: p.source.id, tag: p.source.tag, grade: p.source.grade, massRate: p.result.release.massRate, volumetricRate: p.result.release.volumetricRate, qc: p.result.qc, lfl: sub.lflVolFrac });
    }
    if (!members.length) continue;
    const rec = new Recorder();
    ar.scenarios = summate({
      members,
      maxSimultaneousPrimary: area.summation?.maxSimultaneousPrimary ?? 'all',
      inefficiencyF: area.ventilation.inefficiencyF,
      airFlow: ar.ventilation.airFlow,
      criticalFraction: settings.criticalConcentrationFraction,
    }, rec);
    ar.steps.push(...rec.steps);
    ar.issues.push(...rec.issues);
  }

  // 4. dilution, zone type and extent for each source
  const results: Record<string, SourceResult> = {};
  for (const p of prepared) {
    results[p.source.id] = p.result;
    const area = areas.get(p.source.areaId);
    if (!area || p.result.qc === undefined || p.result.uw === undefined || p.rec.hasErrors) continue;
    finishSource(p, area, areaResults[area.id]);
  }

  return { engineVersion: ENGINE_VERSION, sources: results, areas: areaResults };
}

function finishSource({ source, rec, result }: Prepared, area: Area, areaResult: AreaResult | undefined): void {
  const qc = result.qc as number;
  const uw = result.uw as number;
  const indoor = area.kind === 'indoor';

  // degree of dilution
  let dilution: Dilution;
  if (indoor) {
    const scenario = areaResult?.scenarios[source.grade];
    if (!scenario) {
      rec.issue({ code: 'dilution.no-scenario', severity: 'error', message: 'Background concentration could not be assessed for this room.' });
      return;
    }
    result.scenario = scenario;
    const chart = recordChartDilution(scenario.sumQc, uw, rec, scenario.members.length > 1 ? 'Degree of dilution (summed releases)' : 'Degree of dilution');
    result.chart = chart;
    dilution = chart.degree;
    result.dilutionBasis = 'chart';
    if (scenario.exceeds) {
      // C.3.5: above Xcrit the degree of dilution is low, whatever the chart says.
      dilution = 'low';
      result.dilutionBasis = 'background';
      rec.step({
        id: 'dilution.background',
        label: 'Degree of dilution set by background concentration',
        ref: { clause: 'C.3.5' },
        inputs: { Xb: q(scenario.xb, 'vol/vol'), Xcrit: q(scenario.xcrit, 'vol/vol') },
        output: { symbol: 'dilution', text: 'Low' },
        notes: [{ kind: 'info', text: 'Background concentration above Xcrit: the degree of dilution is low regardless of the chart.' }],
      });
    }
  } else {
    const chart = recordChartDilution(qc, uw, rec);
    result.chart = chart;
    dilution = chart.degree;
    result.dilutionBasis = 'chart';
    const v = area.ventilation;
    if (dilution === 'low' && v.kind === 'outdoor' && !v.obstructed) {
      dilution = 'medium';
      result.dilutionBasis = 'outdoor-rule';
      rec.step({
        id: 'dilution.outdoor',
        label: 'Open air, unobstructed',
        ref: { clause: 'C.3.5' },
        inputs: {},
        output: { symbol: 'dilution', text: 'Medium' },
        notes: [{ kind: 'info', text: 'Low dilution does not generally occur in unobstructed open air; classified as medium.' }],
      });
    }
  }
  const dOverride = source.overrides?.dilution;
  if (dOverride) {
    rec.step({
      id: 'dilution.override',
      label: 'Degree of dilution set by the engineer',
      ref: { clause: '10.1' },
      inputs: {},
      output: { symbol: 'dilution', text: capitalise(dOverride.value) },
      notes: [{ kind: 'decision', text: dOverride.justification || 'No justification recorded.' }],
    });
    if (!dOverride.justification.trim()) rec.issue({ code: 'override.justification', severity: 'warning', field: 'overrides.dilution', message: 'Record why the degree of dilution was overridden.' });
    dilution = dOverride.value;
    result.dilutionBasis = 'override';
  }
  result.dilution = dilution;

  // availability
  let availability = area.availability.level;
  if (indoor && area.ventilation.kind === 'indoor' && area.ventilation.type === 'natural' && availability === 'good') {
    rec.issue({ code: 'availability.natural-good', severity: 'error', field: 'area.availability', message: 'Natural ventilation indoors cannot have good availability. Fair is used until this is changed.', ref: { clause: 'C.3.7.1' } });
    availability = 'fair';
  }
  result.availability = availability;

  // zone type
  const outcome = zoneOutcome(source.grade, dilution, availability);
  result.outcome = outcome;
  result.outcomeLabel = describeOutcome(outcome);
  rec.step({
    id: 'zone.type',
    label: 'Type of zone',
    ref: { clause: 'D.2', table: 'D.1' },
    inputs: {},
    output: { symbol: 'zone', text: result.outcomeLabel },
    notes: [{ kind: 'info', text: `${capitalise(source.grade)} grade, ${dilution} dilution, ${availability} availability.` }],
  });

  if (outcome.kind === 'non-hazardous') {
    result.negligible = outcome.negligible;
    result.zoneLabel = result.outcomeLabel;
    result.complete = !rec.hasErrors;
    return;
  }

  // extent
  const ext = sourceExtent(source, area, dilution, qc, rec, result);
  result.extent = ext;

  const roomVolume = (zone: ZoneNumber, role: ZoneVolume['role']): ZoneVolume => ({ zone, role, horizontalM: null, verticalM: null, roomFill: true });
  const localVolume = (zone: ZoneNumber, role: ZoneVolume['role']): ZoneVolume =>
    ext.basis === 'room' ? roomVolume(zone, role) : { zone, role, horizontalM: ext.horizontalM, verticalM: ext.verticalM, roomFill: false };

  switch (outcome.kind) {
    case 'zone':
      result.volumes = [localVolume(outcome.zone, 'single')];
      if (outcome.negligible !== undefined) result.negligible = outcome.negligible;
      result.zoneLabel = result.outcomeLabel;
      break;
    case 'judgement': {
      const id = 'zone-choice';
      const answer = source.judgements?.[id];
      result.prompts.push({
        id,
        question: NOTE_TEXT[outcome.note],
        options: outcome.options.map((z) => ({ value: String(z), label: `Zone ${z}` })),
        ...(answer ? { answer } : {}),
        ref: { clause: 'D.2', table: 'D.1' },
      });
      const chosen = (answer === '0' ? 0 : answer === '1' ? 1 : outcome.options[0]) as ZoneNumber;
      if (!answer) rec.issue({ code: 'judgement.zone', severity: 'warning', message: `Table D.1 leaves the zone to the engineer here (${result.outcomeLabel}). Zone ${chosen} is shown until you choose.`, ref: { clause: 'D.2', table: 'D.1' } });
      result.volumes = [localVolume(chosen, 'single')];
      result.zoneLabel = `Zone ${chosen}`;
      break;
    }
    case 'nested': {
      const outer = source.overrides?.outerExtentM;
      let outerVol: ZoneVolume;
      if (outer) {
        outerVol = { zone: outcome.outer, role: 'outer', horizontalM: outer.value, verticalM: outer.value, roomFill: false };
      } else if (indoor) {
        outerVol = roomVolume(outcome.outer, 'outer');
        rec.issue({ code: 'nested.outer-room', severity: 'info', message: `The outer Zone ${outcome.outer} fills the room until you enter its extent.`, ref: { clause: 'D.2', table: 'D.1', md: 'MD-005' } });
      } else {
        outerVol = { zone: outcome.outer, role: 'outer', horizontalM: null, verticalM: null, roomFill: false };
        rec.issue({ code: 'nested.outer-missing', severity: 'warning', field: 'overrides.outerExtentM', message: `Enter the extent of the outer Zone ${outcome.outer} (for when ventilation fails).`, ref: { clause: 'D.2', table: 'D.1', md: 'MD-005' } });
      }
      result.volumes = [localVolume(outcome.inner, 'inner'), outerVol];
      result.zoneLabel = result.outcomeLabel;
      break;
    }
  }
  result.complete = !rec.hasErrors;
}

function sourceExtent(
  source: Source,
  area: Area,
  dilution: Dilution,
  qc: number,
  rec: Recorder,
  result: SourceResult,
): NonNullable<SourceResult['extent']> {
  const indoor = area.kind === 'indoor';
  if (indoor && dilution === 'low') {
    rec.step({
      id: 'extent.room',
      label: 'Extent: the whole room',
      ref: { clause: 'C.4.3' },
      inputs: {},
      output: { symbol: 'extent', text: 'Whole enclosed volume' },
      notes: [{ kind: 'info', text: 'With low dilution indoors the enclosed space takes a single classification (C.4.3; E.2 Examples 2 and 5).' }],
    });
    return { horizontalM: null, verticalM: null, basis: 'room' };
  }
  if (indoor && dilution === 'medium') {
    const id = 'indoor-medium-extent';
    const answer = source.judgements?.[id];
    result.prompts.push({
      id,
      question: 'Fig. D.1 assumes zero background concentration and is not applicable indoors at medium dilution (D.3). Classify the whole room, or keep a local zone sized from the chart?',
      options: [
        { value: 'room', label: 'Whole room' },
        { value: 'local', label: 'Local zone from the chart' },
      ],
      ...(answer ? { answer } : {}),
      ref: { clause: 'D.3', md: 'MD-001' },
    });
    if (answer === 'room') {
      rec.step({ id: 'extent.room', label: 'Extent: the whole room', ref: { clause: 'C.3.6.1', md: 'MD-001' }, inputs: {}, output: { symbol: 'extent', text: 'Whole enclosed volume' }, notes: [{ kind: 'decision', text: 'Chosen by the engineer.' }] });
      return { horizontalM: null, verticalM: null, basis: 'room' };
    }
    if (!answer) rec.issue({ code: 'judgement.indoor-medium', severity: 'warning', message: 'Decide whether this source classifies the whole room. A local zone from Fig. D.1 is shown until you do.', ref: { clause: 'D.3', md: 'MD-001' } });
  }

  let curve = source.releaseForm;
  if (curve === 'jet' && !source.releaseFormConfirmed) {
    rec.issue({ code: 'extent.jet-unconfirmed', severity: 'warning', field: 'releaseFormConfirmed', message: 'The jet line applies only to an unimpeded high-velocity release. The diffusive line is used until you confirm it.', ref: { clause: 'D.3' } });
    curve = 'diffusive';
  }
  const h = recordChartExtent(qc, curve, rec, 'horizontal');
  let horizontal = h.distance;
  let vertical = horizontal;
  if (curve === 'heavy') vertical = recordChartExtent(qc, 'diffusive', rec, 'vertical').distance;
  if (h.status === 'above-range' && !source.overrides?.extentHorizontalM) {
    rec.issue({ code: 'extent.above-range', severity: 'error', field: 'overrides.extentHorizontalM', message: 'The release is beyond the range of Fig. D.1. Work out the extent with another method and enter it.', ref: { clause: 'D.3' } });
  }
  let basis: 'chart' | 'override' = 'chart';
  const oh = source.overrides?.extentHorizontalM;
  const ov = source.overrides?.extentVerticalM;
  if (oh) {
    horizontal = oh.value;
    if (!ov) vertical = curve === 'heavy' ? vertical : oh.value;
    basis = 'override';
    rec.step({ id: 'extent.override.horizontal', label: 'Horizontal extent set by the engineer', ref: { clause: '10.1' }, inputs: {}, output: { symbol: 'r', ...q(oh.value, 'm') }, notes: [{ kind: 'decision', text: oh.justification || 'No justification recorded.' }] });
  }
  if (ov) {
    vertical = ov.value;
    basis = 'override';
    rec.step({ id: 'extent.override.vertical', label: 'Vertical extent set by the engineer', ref: { clause: '10.1' }, inputs: {}, output: { symbol: 'r (vertical)', ...q(ov.value, 'm') }, notes: [{ kind: 'decision', text: ov.justification || 'No justification recorded.' }] });
  }
  return { horizontalM: horizontal, verticalM: vertical, status: h.status, basis };
}
